import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Subscription,
    getSubscriptionPackages,
    SubscriptionPackage,
    createSubscriptionCheckout,
} from 'api/rest/myaliro_billing';
import Button from 'views/Dashboards-B2B/components/Button';

interface Package {
    type: string;
    name: string;
    monthlyPrice: number;
    annualPrice: number;
    freePeriod?: string;
    requiresApproval: boolean;
    features: string[];
    caseFeeModel: string;
    stripeProductId: string;
    stripeMonthlyPriceId: string;
    stripeAnnualPriceId?: string;
}

interface ChangePackageModalProps {
    open: boolean;
    currentSubscription: Subscription | null;
    hasPaymentMethods?: boolean;
    onClose: () => void;
    onConfirm: (newPackageType: string, newBillingPeriod: 'MONTHLY' | 'ANNUAL') => void;
    useStripeCheckout?: boolean; // New prop to enable Stripe Checkout flow
}

const ChangePackageModal: React.FC<ChangePackageModalProps> = ({
    open,
    currentSubscription,
    hasPaymentMethods = false,
    onClose,
    onConfirm,
    useStripeCheckout = true, // Default to Stripe Checkout
}) => {
    // Initialize billing period based on current subscription, default to MONTHLY
    const [billingPeriod, setBillingPeriod] = useState<'MONTHLY' | 'ANNUAL'>(() => {
        if (!currentSubscription?.billing_period) return 'MONTHLY';
        // Convert Stripe format ('month', 'year') to component format ('MONTHLY', 'ANNUAL')
        return currentSubscription.billing_period === 'year' ? 'ANNUAL' : 'MONTHLY';
    });
    const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
    const [packages, setPackages] = useState<Package[]>([]);
    const [isLoadingPackages, setIsLoadingPackages] = useState(true);
    const [packagesError, setPackagesError] = useState<string | null>(null);
    const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

    // Fetch subscription packages from Stripe
    useEffect(() => {
        const fetchPackages = async () => {
            try {
                setIsLoadingPackages(true);
                setPackagesError(null);
                const response = await getSubscriptionPackages();

                // Transform SubscriptionPackage to Package format
                const transformedPackages: Package[] = response.data.packages.map(
                    (pkg: SubscriptionPackage) => ({
                        type: pkg.type,
                        name: pkg.name,
                        monthlyPrice: pkg.monthly_price,
                        annualPrice: pkg.annual_price,
                        freePeriod: pkg.free_period,
                        requiresApproval: pkg.requires_approval,
                        features: pkg.features,
                        caseFeeModel: pkg.case_fee_model,
                        stripeProductId: pkg.stripe_product_id,
                        stripeMonthlyPriceId: pkg.stripe_monthly_price_id,
                        stripeAnnualPriceId: pkg.stripe_annual_price_id,
                    })
                );

                setPackages(transformedPackages);
            } catch (error) {
                console.error('Failed to fetch subscription packages:', error);
                setPackagesError('Failed to load subscription packages. Please try again.');
            } finally {
                setIsLoadingPackages(false);
            }
        };

        if (open) {
            fetchPackages();
        }
    }, [open]);

    // Update billing period when subscription changes
    React.useEffect(() => {
        if (currentSubscription?.billing_period) {
            // Convert Stripe format ('month', 'year') to component format ('MONTHLY', 'ANNUAL')
            const period = currentSubscription.billing_period === 'year' ? 'ANNUAL' : 'MONTHLY';
            setBillingPeriod(period);
        }
    }, [currentSubscription?.billing_period]);

    // Clear selected package when billing period changes if package not available for new period
    React.useEffect(() => {
        if (selectedPackage) {
            const selectedPkg = packages.find((p) => p.type === selectedPackage);
            if (selectedPkg) {
                const isAvailable =
                    billingPeriod === 'ANNUAL'
                        ? selectedPkg.stripeAnnualPriceId
                        : selectedPkg.stripeMonthlyPriceId;
                if (!isAvailable) {
                    setSelectedPackage(null);
                }
            }
        }
    }, [billingPeriod, selectedPackage, packages]);

    if (!open) return null;

    const handleConfirm = async () => {
        if (!selectedPackage) return;

        // Always use Stripe Checkout for both new subscriptions and package changes
        try {
            setIsCreatingCheckout(true);

            // Create Stripe Checkout session
            const baseUrl = window.location.origin;
            const response = await createSubscriptionCheckout({
                package_type: selectedPackage,
                billing_period: billingPeriod,
                success_url: `${baseUrl}/account/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
                cancel_url: `${baseUrl}/account/financials?payment=cancelled`,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                currency: 'USD',
            });

            // Redirect to Stripe Checkout
            window.location.href = response.data.checkout_url;
        } catch (error) {
            console.error('Failed to create checkout session:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            alert(`Failed to create checkout session: ${errorMessage}`);
            setIsCreatingCheckout(false);
        }
    };

    const getPrice = (pkg: Package): number => {
        return billingPeriod === 'ANNUAL' ? pkg.annualPrice : pkg.monthlyPrice;
    };

    const getPriceDisplay = (pkg: Package): string => {
        const price = getPrice(pkg);
        if (pkg.type === 'SPECIAL_PARTNER') return '$0';
        return `$${price}`;
    };

    const getPeriodDisplay = (): string => {
        return billingPeriod === 'ANNUAL' ? '/year' : '/month';
    };

    // Filter packages based on selected billing period
    const availablePackages = packages.filter((pkg) => {
        if (billingPeriod === 'ANNUAL') {
            // Only show packages that have an annual price ID
            return pkg.stripeAnnualPriceId !== null && pkg.stripeAnnualPriceId !== undefined;
        }
        // Monthly packages should always have a monthly price ID
        return pkg.stripeMonthlyPriceId !== null && pkg.stripeMonthlyPriceId !== undefined;
    });

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose}>✕</CloseButton>

                <ModalHeader>
                    <ModalTitle>
                        {currentSubscription ? 'Change Package' : 'Create Subscription'}
                    </ModalTitle>
                </ModalHeader>

                {!currentSubscription && (
                    <InfoSection>
                        <InfoText type="info">
                            Choose your subscription package below. Default settings will be
                            applied: Billed on the 1st of each month, USD currency, auto-detected
                            timezone. You can change these settings later in Billing Settings.
                        </InfoText>
                    </InfoSection>
                )}

                <BillingToggleSection>
                    <BillingToggleLabel>Billing Period:</BillingToggleLabel>
                    <BillingToggle>
                        <ToggleOption
                            active={billingPeriod === 'MONTHLY'}
                            onClick={() => setBillingPeriod('MONTHLY')}
                        >
                            Monthly
                        </ToggleOption>
                        <ToggleOption
                            active={billingPeriod === 'ANNUAL'}
                            onClick={() => setBillingPeriod('ANNUAL')}
                        >
                            Annual
                            <DiscountBadge>Save 18%</DiscountBadge>
                        </ToggleOption>
                    </BillingToggle>
                </BillingToggleSection>

                {isLoadingPackages && (
                    <LoadingState>
                        <LoadingSpinner />
                        <LoadingText>Loading subscription packages...</LoadingText>
                    </LoadingState>
                )}

                {packagesError && (
                    <ErrorState>
                        <ErrorText>{packagesError}</ErrorText>
                        <Button background="purple" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </ErrorState>
                )}

                {!isLoadingPackages && !packagesError && packages.length === 0 && (
                    <EmptyPackagesState>
                        <EmptyText>No subscription packages available at this time.</EmptyText>
                    </EmptyPackagesState>
                )}

                {!isLoadingPackages &&
                    !packagesError &&
                    availablePackages.length === 0 &&
                    packages.length > 0 && (
                        <EmptyPackagesState>
                            <EmptyText>
                                No packages available for {billingPeriod.toLowerCase()} billing.
                                Please select a different billing period.
                            </EmptyText>
                        </EmptyPackagesState>
                    )}

                {!isLoadingPackages && !packagesError && availablePackages.length > 0 && (
                    <PackagesGrid>
                        {availablePackages.map((pkg) => {
                            // Determine if this is the current package AND billing period
                            const isSameProduct =
                                currentSubscription?.product_id === pkg.stripeProductId;
                            const currentBillingPeriod =
                                billingPeriod === 'ANNUAL' ? 'year' : 'month';
                            const isCurrent =
                                isSameProduct &&
                                currentSubscription?.billing_period === currentBillingPeriod;
                            const isSelected = selectedPackage === pkg.type;

                            // Check if this is the same package but different billing period
                            const isSwitchBillingPeriod = isSameProduct && !isCurrent;

                            // Determine if this is an upgrade, downgrade, or billing period switch
                            let changeType: 'upgrade' | 'downgrade' | 'switch' | null = null;
                            if (currentSubscription && !isCurrent) {
                                // If same product but different billing period, it's a switch
                                if (isSwitchBillingPeriod) {
                                    changeType = 'switch';
                                } else {
                                    // Normalize prices to monthly equivalent for fair comparison
                                    const currentMonthlyEquivalent =
                                        currentSubscription.billing_period === 'year'
                                            ? currentSubscription.amount / 12
                                            : currentSubscription.amount;

                                    const newPrice = getPrice(pkg);
                                    const newMonthlyEquivalent =
                                        billingPeriod === 'ANNUAL' ? newPrice / 12 : newPrice;

                                    if (newMonthlyEquivalent > currentMonthlyEquivalent) {
                                        changeType = 'upgrade';
                                    } else if (newMonthlyEquivalent < currentMonthlyEquivalent) {
                                        changeType = 'downgrade';
                                    }
                                }
                            }

                            return (
                                <PackageCard
                                    key={pkg.type}
                                    onClick={() => !isCurrent && setSelectedPackage(pkg.type)}
                                    selected={isSelected}
                                    current={isCurrent}
                                >
                                    {isCurrent && <CurrentBadge>Current Plan</CurrentBadge>}
                                    {pkg.requiresApproval && (
                                        <ApprovalBadge>Requires Approval</ApprovalBadge>
                                    )}

                                    <PackageName>{pkg.name}</PackageName>

                                    <PriceSection>
                                        <Price>{getPriceDisplay(pkg)}</Price>
                                        {pkg.type !== 'SPECIAL_PARTNER' && (
                                            <PricePeriod>{getPeriodDisplay()}</PricePeriod>
                                        )}
                                    </PriceSection>

                                    {pkg.freePeriod && (
                                        <FreePeriodText>{pkg.freePeriod}</FreePeriodText>
                                    )}

                                    <CaseFeeText>{pkg.caseFeeModel}</CaseFeeText>

                                    <FeaturesList>
                                        {pkg.features.map((feature, idx) => (
                                            <FeatureItem key={idx}>✓ {feature}</FeatureItem>
                                        ))}
                                    </FeaturesList>

                                    {!isCurrent && (
                                        <SelectButton selected={isSelected} changeType={changeType}>
                                            {isSelected
                                                ? 'Selected'
                                                : changeType === 'switch'
                                                  ? `Switch to ${billingPeriod === 'ANNUAL' ? 'Annual' : 'Monthly'}`
                                                  : changeType === 'upgrade'
                                                    ? 'Upgrade'
                                                    : changeType === 'downgrade'
                                                      ? 'Downgrade'
                                                      : 'Select'}
                                        </SelectButton>
                                    )}
                                </PackageCard>
                            );
                        })}
                    </PackagesGrid>
                )}

                {selectedPackage && !isLoadingPackages && (
                    <InfoSection>
                        {packages.find((p) => p.type === selectedPackage)?.requiresApproval ? (
                            <InfoText type="warning">
                                This package requires admin approval. You'll be notified once your
                                request has been reviewed.
                            </InfoText>
                        ) : (
                            <InfoText type="info">
                                {currentSubscription
                                    ? "You'll be redirected to Stripe Checkout to complete the package change. Your existing subscription will be cancelled and a new one will be created with prorated billing."
                                    : "You'll be redirected to Stripe Checkout to complete your subscription setup and add payment information."}
                            </InfoText>
                        )}
                    </InfoSection>
                )}

                <ButtonGroup>
                    <Button
                        background="transparent"
                        border="1px solid #d1d5db"
                        color="#374151"
                        onClick={onClose}
                        disabled={isCreatingCheckout}
                    >
                        Cancel
                    </Button>
                    <Button
                        background="purple"
                        onClick={handleConfirm}
                        disabled={!selectedPackage || isCreatingCheckout}
                    >
                        {isCreatingCheckout ? 'Redirecting to Stripe...' : 'Continue to Checkout'}
                    </Button>
                </ButtonGroup>
            </ModalContent>
        </ModalOverlay>
    );
};

export default ChangePackageModal;

// Styled Components
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: #ffffff;
    border-radius: 12px;
    max-width: 1200px;
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    padding: 32px;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6b7280;
    padding: 4px 8px;

    &:hover {
        color: #111827;
    }
`;

const ModalHeader = styled.div`
    margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin: 0;
`;

const BillingToggleSection = styled.div`
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    gap: 16px;
`;

const BillingToggleLabel = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #374151;
`;

const BillingToggle = styled.div`
    display: flex;
    background: #f3f4f6;
    border-radius: 8px;
    padding: 4px;
`;

const ToggleOption = styled.button<{ active: boolean }>`
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
    background: ${(props) => (props.active ? '#6366f1' : 'transparent')};
    color: ${(props) => (props.active ? '#ffffff' : '#6b7280')};

    &:hover {
        background: ${(props) => (props.active ? '#5558e3' : '#e5e7eb')};
    }
`;

const DiscountBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    background: #10b981;
    color: #ffffff;
`;

const PackagesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
`;

const PackageCard = styled.div<{ selected: boolean; current: boolean }>`
    border: 2px solid
        ${(props) => (props.selected ? '#6366f1' : props.current ? '#10b981' : '#e5e7eb')};
    border-radius: 12px;
    padding: 20px;
    cursor: ${(props) => (props.current ? 'default' : 'pointer')};
    position: relative;
    transition: all 0.2s;
    background: ${(props) => (props.selected ? '#eff6ff' : props.current ? '#f0fdf4' : '#ffffff')};
    opacity: ${(props) => (props.current ? 0.7 : 1)};

    &:hover {
        border-color: ${(props) => (props.current ? '#10b981' : '#6366f1')};
        transform: ${(props) => (props.current ? 'none' : 'translateY(-2px)')};
        box-shadow: ${(props) => (props.current ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.1)')};
    }
`;

const CurrentBadge = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    background: #10b981;
    color: #ffffff;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
`;

const ApprovalBadge = styled.div`
    background: #fef3c7;
    color: #92400e;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 12px;
    text-align: center;
`;

const PackageName = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 16px 0;
`;

const PriceSection = styled.div`
    margin-bottom: 8px;
`;

const Price = styled.span`
    font-size: 32px;
    font-weight: 700;
    color: #6366f1;
`;

const PricePeriod = styled.span`
    font-size: 14px;
    color: #6b7280;
    margin-left: 4px;
`;

const FreePeriodText = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #f59e0b;
    margin-bottom: 12px;
`;

const CaseFeeText = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 16px;
    font-weight: 500;
`;

const FeaturesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 16px 0;
`;

const FeatureItem = styled.li`
    font-size: 13px;
    color: #374151;
    margin-bottom: 8px;
    padding-left: 4px;
`;

const SelectButton = styled.button<{
    selected: boolean;
    changeType?: 'upgrade' | 'downgrade' | 'switch' | null;
}>`
    width: 100%;
    padding: 10px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    background: ${(props) => {
        if (props.selected) return '#6366f1';
        if (props.changeType === 'upgrade') return '#10b981';
        if (props.changeType === 'downgrade') return '#f59e0b';
        if (props.changeType === 'switch') return '#6366f1';
        return '#e5e7eb';
    }};
    color: ${(props) => {
        if (props.selected) return '#ffffff';
        if (
            props.changeType === 'upgrade' ||
            props.changeType === 'downgrade' ||
            props.changeType === 'switch'
        )
            return '#ffffff';
        return '#374151';
    }};
    transition: all 0.2s;

    &:hover {
        background: ${(props) => {
            if (props.selected) return '#5558e3';
            if (props.changeType === 'upgrade') return '#059669';
            if (props.changeType === 'downgrade') return '#d97706';
            if (props.changeType === 'switch') return '#5558e3';
            return '#d1d5db';
        }};
    }
`;

const InfoSection = styled.div`
    margin-bottom: 24px;
`;

const InfoText = styled.div<{ type: 'info' | 'warning' }>`
    padding: 12px 16px;
    border-radius: 6px;
    font-size: 14px;
    background: ${(props) => (props.type === 'warning' ? '#fef3c7' : '#eff6ff')};
    color: ${(props) => (props.type === 'warning' ? '#92400e' : '#1e40af')};
    border: 1px solid ${(props) => (props.type === 'warning' ? '#fbbf24' : '#bfdbfe')};
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
`;

const LoadingState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    gap: 16px;
`;

const LoadingSpinner = styled.div`
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const LoadingText = styled.div`
    font-size: 14px;
    color: #6b7280;
    font-weight: 500;
`;

const ErrorState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    gap: 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    margin: 24px 0;
`;

const ErrorText = styled.div`
    font-size: 14px;
    color: #991b1b;
    font-weight: 500;
    text-align: center;
`;

const EmptyPackagesState = styled.div`
    padding: 48px 24px;
    text-align: center;
    background: #f9fafb;
    border-radius: 8px;
    margin: 24px 0;
`;

const EmptyText = styled.div`
    font-size: 14px;
    color: #6b7280;
    font-weight: 500;
`;
